import { Element, useNode } from "@craftjs/core";
import { rem } from "polished";
import * as React from "react";
import useViewport from "src/hooks/use-viewport";
import type { TextAlign, Viewport } from "src/types";
import getViewportValue from "src/utils/get-viewport-value";
import { ThemeContext } from "styled-components";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import Action, {
  ActionComponentStyle,
  ACTIONS,
} from "@/components/editor/primitives/legacy/action/action-component";
import RichText from "@/components/editor/primitives/rich-text";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
} from "@/components/editor/shared/block-wrapper";
import { DEFAULT_BUTTON_TEXT } from "@/components/editor/shared/constants";
import { blackLogo, whiteLogo } from "@/components/editor/shared/header/assets";
import formatLogoSize from "@/components/editor/shared/header/format-logo-size";
import HeaderSettings, {
  ImageFit,
} from "@/components/editor/shared/header/header-settings-component";
import {
  ColorLabels,
  useInvertedColor,
} from "@/components/editor/utils/use-color";
import WrappedElement from "@/components/element/element-component";
import useRichTextCompatible from "@/hooks/use-rich-text-compatible";

import {
  BackgroundImage,
  ContentWrapper,
  Logo,
  LogoWrapper,
} from "./header-option-3-component-styles";

type HeaderOption3Props = {
  heading: string;
  subheading: string;
  ctaText: string;
  logo?: string;
  isLogoShowing?: boolean;
  backgroundImage?: string;
  backgroundPosition?: ImagePosition;
  logoSize?: number;
  imageFit?: ImageFit;
};

export default function HeaderOption3(props: HeaderOption3Props) {
  const {
    connectors: { connect },
    id: nodeId,
  } = useNode();

  const viewport = useViewport();
  const theme = React.useContext(ThemeContext);
  const style = useStyle(viewport);

  const isRichTextCompatible = useRichTextCompatible(nodeId, [
    "heading",
    "subheading",
    "cta-text",
  ]);

  const showLightLogo =
    useInvertedColor(theme.colors[ColorLabels.BACKGROUND]) === "#ffffff";
  const defaultLogoImage = showLightLogo ? whiteLogo : blackLogo;

  return (
    <BlockBackgroundColorWrapper
      ref={connect}
      id={nodeId}
      style={style.wrapper}
      data-testid="header-option-3"
    >
      <BackgroundImage
        $backgroundImage={props.backgroundImage}
        backgroundPosition={props.backgroundPosition}
        imageFit={props.imageFit}
      />
      <BlockMaxWidthWrapper>
        <ContentWrapper>
          {props.isLogoShowing && (
            <LogoWrapper>
              <Logo
                draggable="false"
                src={props.logo || defaultLogoImage}
                alt="Header Logo"
                data-testid="header-option-3-logo"
                style={{
                  height: formatLogoSize(props.logoSize),
                  width: "auto",
                }}
                isLogoResized={!isNaN(props.logoSize)}
              />
            </LogoWrapper>
          )}
          <div style={style.layout}>
            <div style={style.col1}>
              {isRichTextCompatible ? (
                <>
                  <Element
                    id="heading"
                    is={RichText}
                    text={`<h2>${props.heading}</h2>`}
                  />
                  {viewport === "desktop" && (
                    <Element
                      id="cta-text"
                      is={RichText}
                      text={`<h5>${props.ctaText}</h5>`}
                    />
                  )}
                </>
              ) : (
                <>
                  <WrappedElement
                    id="heading"
                    is={Text}
                    element="h1"
                    text={props.heading}
                    pattern="headingOne"
                  />
                  {viewport === "desktop" && (
                    <WrappedElement
                      id="cta-text"
                      is={Text}
                      text={props.ctaText}
                      pattern="headingFour"
                    />
                  )}
                </>
              )}
            </div>
            <div style={style.col2}>
              {isRichTextCompatible ? (
                <>
                  <Element
                    id="subheading"
                    is={RichText}
                    text={`<p>${props.subheading}</p>`}
                  />
                  {viewport !== "desktop" && (
                    <Element
                      id="cta-text"
                      is={RichText}
                      text={`<h5>${props.ctaText}</h5>`}
                    />
                  )}
                </>
              ) : (
                <>
                  <WrappedElement
                    id="subheading"
                    is={Text}
                    text={props.subheading}
                    pattern="paragraph"
                  />
                  {viewport !== "desktop" && (
                    <WrappedElement
                      id="cta-text"
                      is={Text}
                      text={props.ctaText}
                      pattern="headingFour"
                    />
                  )}
                </>
              )}
              <div style={style.ctaButtonWrapper}>
                <WrappedElement
                  id="cta-button"
                  is={Action}
                  element="button"
                  text={DEFAULT_BUTTON_TEXT}
                  style={style.ctaButton}
                  action={ACTIONS.registrationPage}
                  actions={[ACTIONS.registrationPage, ACTIONS.externalLink]}
                  showOptions
                />
              </div>
            </div>
          </div>
        </ContentWrapper>
      </BlockMaxWidthWrapper>
    </BlockBackgroundColorWrapper>
  );
}

const useStyle = (viewport: Viewport) => {
  const style = React.useMemo(
    () => ({
      wrapper: {
        display: "grid",
        gridTemplateRows: getViewportValue(
          [`${rem(170)} auto`, `${rem(200)} auto`, `${rem(300)} auto`],
          viewport
        ),
      },
      layout: {
        display: "grid",
        gridTemplateColumns: getViewportValue(
          ["1fr", "1fr", "1fr 1fr"],
          viewport
        ),
        gridColumnGap: rem(24),
      },
      col1: {
        display: "grid",
        gridTemplateRows: "min-content min-content",
        gridRowGap: getViewportValue([rem(16), rem(16), rem(40)], viewport),
      },
      col2: {
        display: "grid",
        gridTemplateRows: "min-content min-content",
        gridRowGap: getViewportValue([rem(24), rem(24), rem(32)], viewport),
      },
      ctaButtonWrapper: {
        width: "fit-content",
        margin: getViewportValue(
          ["auto", "auto", "unset"],
          viewport
        ) as TextAlign,
      } as React.CSSProperties,
      ctaButton: {
        padding: [rem(12), rem(12), rem(12)],
      } as ActionComponentStyle,
    }),
    [viewport]
  );

  return style;
};

const CustomHeaderSettings = () => (
  <HeaderSettings
    optimalImageSize="1400x300px"
    backgroundImageSize={{
      width: "100%",
      height: "200",
    }}
    disableBackgroundImageAlt
  />
);

HeaderOption3.craft = {
  custom: {
    displayName: "Header option #3",
  },
  related: {
    settings: CustomHeaderSettings,
    leftOutlineOptions: TopLevelFloatingOptions,
  },
};
